import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import type {
  Device,
  AvailableVehicle,
} from "../../store/admin/devicesStore";

export interface DeviceListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface DeviceListResponse {
  data: Device[];
  meta: ListMeta;
}

const ALL_DEVICES_LIMIT = 1000;

export function fetchAllDevices(
  companyId: string | null,
  signal?: AbortSignal,
): Promise<Device[]> {
  return getJson<DeviceListResponse>(
    "/deviceList",
    { limit: ALL_DEVICES_LIMIT, page: 1, company_id: companyId ?? "" },
    { signal },
    { headers: { "x-company-id": companyId ?? "" } },
  ).then((res) => res.data ?? []);
}

export function allDevicesQueryOptions(companyId: string | null) {
  return {
    queryKey: qk.devices.all(companyId),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchAllDevices(companyId, signal),
    staleTime: 5 * 60_000,
    enabled: !!companyId,
  };
}

export function useAllDevices() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const companyUser = useCompaniesStore((s) => s.companyUser);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId || companyUser;
  return useQuery(allDevicesQueryOptions(cid));
}

export function useDevicesList(params: DeviceListParams = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.devices.list(companyId, params),
    queryFn: ({ signal }) =>
      getJson<DeviceListResponse>(
        "/deviceList",
        { page: 1, limit: 8, ...params },
        { signal },
      ),
  });
}

export function useAvailableVehicles() {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.vehicles.available(companyId),
    queryFn: ({ signal }) =>
      getJson<{ data: AvailableVehicle[] }>(
        "/vehicleListWithoutDeviceSelected",
        {},
        { signal },
      ).then((res) => res.data ?? []),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

export interface AvailableTrailer {
  id: string;
  name?: string;
  plate?: string;
}

function unwrapTrailerList(raw: unknown): AvailableTrailer[] {
  if (Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown> | null;
  const firstLevel = obj?.data;
  if (Array.isArray(firstLevel)) return firstLevel;
  const secondLevel = (firstLevel as Record<string, unknown> | null)?.data;
  if (Array.isArray(secondLevel)) return secondLevel;
  return [];
}

export function useAvailableTrailers() {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.trailers.available(companyId),
    queryFn: ({ signal }) =>
      getJson<unknown>("/remolquesForVehicle", {}, { signal }).then(unwrapTrailerList),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

export function useDevicesLight(search: string) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.devices.light(companyId, search),
    queryFn: ({ signal }) =>
      getJson<DeviceListResponse>(
        "/deviceList",
        { page: 1, limit: ALL_DEVICES_LIMIT, search },
        { signal },
      ).then((res) => res.data ?? []),
    staleTime: 60_000,
  });
}
